import { useContext } from 'react';
import { hasRichFormatting } from '../../../helpers/rich_text';
import { GENDER_OPTION_DEFAULTS, componentCopy, localizeQuestion } from '../../../helpers/questionnaire_translations';

import React from 'react';
import RadioPillQuestion from '../../core/compositions/radio_pill_question';
import CheckboxPillQuestion from '../../core/compositions/checkbox_pill_question';
import DateOfBirthQuestion from '../../core/compositions/date_of_birth_question';
import AcknowledgmentQuestion from '../../core/compositions/acknowledgment_question';
import QuestionWrapper from '../../core/ui/question_wrapper';
import QuestionHeading from '../../core/ui/question_heading';
import QuestionSubheading from '../../core/ui/question_subheading';
import TextInput from '../../core/ui/text_input';
import RichText from '../../core/ui/rich_text';
import DocumentSigningQuestion from './document_signing_question';
import MentorMatchQuestion from './mentor_match_question';
import ClinicalSiteQuestion from './clinical_site_question';
import QuestionnaireLocaleContext from '../locale_context';
import StarRatingQuestion from './star_rating_question';

// The acknowledgment component has no free-form answer. Checked answers are
// stored as "Yes", matching the hardcoded questionnaires (the data blob
// downcases that to "yes"). "acknowledged" is the previous builder token.
export const ACKNOWLEDGED_VALUE = 'Yes';

const acknowledgmentChecked = (answer) =>
  answer === ACKNOWLEDGED_VALUE || answer === 'yes' || answer === 'acknowledged';

// Returns a RichText node for content that carries bold/underline/line-break
// markup, and the plain string otherwise so unformatted (legacy) content keeps
// its existing string-based rendering (e.g. heading auto-scaling).
const renderRich = (value) => (hasRichFormatting(value) ? <RichText html={value} /> : value);

const optionsForComponent = (question) =>
  question.options.map((option) => ({ value: option.value, label: renderRich(option.label) }));

/**
 * Renders one builder-defined question (client shape from
 * helpers/questionnaire_definition.js) using the real v2 question components.
 * Used by both the member-facing dynamic questionnaire flow and the admin
 * builder preview.
 */
const QuestionRenderer = ({
  question: rawQuestion,
  current,
  total,
  answer,
  onAnswerChange,
  onSubmit,
  onBack = () => {},
  otherValue = '',
  onOtherChange,
  onRequestDocusealSubmission,
  onRequestDocusealSigningState,
  onRedoDocusealConsent,
  onDocusealComplete,
  docusealPreviewSlug,
  onRequestMentorMatch,
  onMentorMatchOutcome,
  onSearchSites,
  firstMessageAnswer,
}) => {
  const { locale, translations, chrome } = useContext(QuestionnaireLocaleContext);
  const withDefaults =
    rawQuestion.componentType === 'gender' && (rawQuestion.options || []).length === 0
      ? {
          ...rawQuestion,
          options: GENDER_OPTION_DEFAULTS.map((option) => ({ id: null, label: option.label, value: option.value })),
        }
      : rawQuestion;
  const question = localizeQuestion(withDefaults, translations, locale);
  const copy = componentCopy(question, translations, locale);
  const otherPlaceholder = question.placeholder || chrome('other');
  const shared = {
    heading: renderRich(question.heading || 'Untitled question'),
    subHeading: question.subHeading ? renderRich(question.subHeading) : undefined,
    current,
    total,
    onSubmit,
    onBack,
  };

  switch (question.componentType) {
    case 'radio_pill':
    case 'gender':
    case 'ready_for_next_step':
      if (question.componentType === 'radio_pill' && question.displayAsStars) {
        return (
          <StarRatingQuestion
            {...shared}
            options={optionsForComponent(question)}
            value={typeof answer === 'string' ? answer : ''}
            onChange={onAnswerChange}
            submitDisabledWhenEmpty={!question.optional}
          />
        );
      }
      return (
        <RadioPillQuestion
          {...shared}
          name={question.dataKey || question.ref}
          options={optionsForComponent(question)}
          value={typeof answer === 'string' ? answer : ''}
          onChange={onAnswerChange}
          showOther={question.allowOther}
          otherValue={otherValue}
          onOtherChange={onOtherChange}
          otherPlaceholder={otherPlaceholder}
          submitDisabledWhenEmpty={!question.optional}
        />
      );
    case 'checkbox_pill':
      return (
        <CheckboxPillQuestion
          {...shared}
          name={question.dataKey || question.ref}
          options={optionsForComponent(question)}
          values={Array.isArray(answer) ? answer : []}
          onToggle={(value) => {
            const values = Array.isArray(answer) ? answer : [];
            onAnswerChange(values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value]);
          }}
          onSubmit={() => onSubmit()}
          showOther={question.allowOther}
          otherValue={otherValue}
          onOtherChange={onOtherChange}
          otherPlaceholder={otherPlaceholder}
          submitDisabledWhenEmpty={!question.optional}
        />
      );
    case 'date_of_birth':
      return (
        <DateOfBirthQuestion
          {...shared}
          yearOnly={Boolean(question.yearOfBirth)}
          value={typeof answer === 'string' ? answer : ''}
          onChange={onAnswerChange}
          placeholder={question.yearOfBirth ? copy.year_placeholder : copy.date_placeholder}
          messages={copy}
        />
      );
    case 'acknowledgment':
      return (
        <AcknowledgmentQuestion
          {...shared}
          body={renderRich(question.body)}
          confirmationLabel={copy.confirmation}
          checked={acknowledgmentChecked(answer)}
          onCheckedChange={(checked) => onAnswerChange(checked ? ACKNOWLEDGED_VALUE : '')}
          submitDisabledWhenUnchecked={!question.optional}
        />
      );
    case 'text_input':
      return (
        <QuestionWrapper
          current={current}
          total={total}
          onSubmit={onSubmit}
          onBack={onBack}
          isDisabled={!question.optional && (!answer || String(answer).trim() === '')}
        >
          <QuestionHeading>{shared.heading}</QuestionHeading>
          {shared.subHeading && <QuestionSubheading>{shared.subHeading}</QuestionSubheading>}
          <TextInput
            value={typeof answer === 'string' ? answer : ''}
            onChange={onAnswerChange}
            placeholder={question.placeholder || ''}
            ariaLabel={shared.heading}
          />
        </QuestionWrapper>
      );
    case 'document_signing':
      return (
        <DocumentSigningQuestion
          question={question}
          current={current}
          total={total}
          onRequestSubmission={onRequestDocusealSubmission}
          onRequestSigningState={onRequestDocusealSigningState}
          onRedo={onRedoDocusealConsent}
          onComplete={onDocusealComplete}
          onBack={onBack}
          previewSlug={docusealPreviewSlug}
        />
      );
    case 'clinical_site':
      return (
        <ClinicalSiteQuestion
          question={question}
          current={current}
          total={total}
          value={answer && typeof answer === 'object' ? answer : {}}
          onChange={onAnswerChange}
          onSubmit={onSubmit}
          onBack={onBack}
          onSearchSites={onSearchSites}
          copy={copy}
        />
      );
    case 'mentor_match':
      return (
        <MentorMatchQuestion
          question={question}
          onRequestMatch={onRequestMentorMatch}
          onOutcome={onMentorMatchOutcome}
          firstMessageAnswer={firstMessageAnswer}
        />
      );
    default:
      return null;
  }
};

export default QuestionRenderer;
